import React from 'react';
import { Box, Typography, useTheme, Grid, LinearProgress } from '@mui/material';
import { CloudQueue, InsertDriveFile } from '@mui/icons-material';
import ReactApexChart from 'react-apexcharts';

const WidgetHeader = ({ title, icon: Icon }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            {Icon && <Icon sx={{ color: theme.palette.text.secondary }} />}
            <Typography variant="h6" fontWeight="bold">
                {title}
            </Typography>
        </Box>
    );
};

const StorageAnalytics = ({ data }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const labelColor = theme.palette.text.secondary;

    const totalUsedGB = (data?.totalBytes || 0) / (1024 * 1024 * 1024);
    
    // File Types Treemap or Bar
    // Let's use a Bar chart for file types by Size
    const fileTypes = data?.byType || [];
    const typeSeries = [{
        name: 'Size (MB)',
        data: fileTypes.map(t => (t.totalSize / (1024 * 1024)).toFixed(2))
    }];
    const typeLabels = fileTypes.map(t => t._id || 'Unknown');

    const chartOptions = {
        chart: { type: 'bar', toolbar: { show: false }, background: 'transparent' },
        plotOptions: { bar: { borderRadius: 4, horizontal: true } },
        xaxis: { categories: typeLabels, labels: { style: { colors: labelColor } } },
        yaxis: { labels: { style: { colors: labelColor } } },
        colors: [theme.palette.warning.main],
        grid: { borderColor: theme.palette.divider },
        tooltip: { theme: isDark ? 'dark' : 'light' }
    };

    return (
        <Box sx={{ p: 2, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, height: '100%' }}>
            <WidgetHeader title="Storage Usage" icon={CloudQueue} />
            
            <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Total Used Space</Typography>
                    <Typography variant="body2" fontWeight="bold">{totalUsedGB.toFixed(2)} GB</Typography>
                </Box>
                <LinearProgress variant="determinate" value={Math.min((totalUsedGB / 500) * 100, 100)} sx={{ height: 10, borderRadius: 5, bgcolor: theme.palette.action.hover }} />
            </Box>

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>Usage by File Type</Typography>
            <Box sx={{ minHeight: 200 }}>
                <ReactApexChart options={chartOptions} series={typeSeries} type="bar" height={200} />
            </Box>
        </Box>
    );
};

export default StorageAnalytics;
